import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { apiClient } from '@/app/api/client';
import { useAssistantAction } from '@/contexts/AssistantActionContext';
import { useProfile } from '@/contexts/ProfileContext';
import { ProfileSection } from './index';
import AIAssistantSection from './AIAssistantSection';
import { Button } from '@/components/common/Button';
import FormInput from '@/components/common/form/FormInput';
import Select from '@/components/common/Select';

const EMOJI_OPTIONS = [
    { value: '🧑', label: '🧑 Person' },
    { value: '💼', label: '💼 Business' },
    { value: '🚀', label: '🚀 Launch' },
    { value: '✍️', label: '✍️ Writer' },
    { value: '🌱', label: '🌱 Growth' },
    { value: '📚', label: '📚 Education' },
    { value: '🎨', label: '🎨 Design' },
    { value: '🏢', label: '🏢 Corporate' },
    { value: '💡', label: '💡 Idea' },
    { value: '🌍', label: '🌍 Global' },
    { value: '🎯', label: '🎯 Target' },
    { value: '📝', label: '📝 Draft' },
    { value: '🔬', label: '🔬 Research' },
    { value: '🎬', label: '🎬 Content' },
    { value: '🏆', label: '🏆 Success' },
    { value: '🌟', label: '🌟 Special' },
    { value: '🧪', label: '🧪 Experiment' },
    { value: '💻', label: '💻 Tech' },
    { value: '🤝', label: '🤝 Partners' },
    { value: '🦋', label: '🦋 Creative' },
];

interface PersonaManagementTabProps {
    profile: any;
    onDeleted: () => void;
}

export default function PersonaManagementTab({ profile: p, onDeleted }: PersonaManagementTabProps) {
    const { triggerAssistant } = useAssistantAction();
    const { refreshProfiles, updateProfile, deleteProfile } = useProfile();

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    // Identity state
    const [name, setName] = useState(p.name);
    const [emoji, setEmoji] = useState(p.avatarEmoji);
    const [handle, setHandle] = useState(p.handle || '');

    // Assistant / AI Advisor state (AI DNA)
    const [assistant, setAssistant] = useState(p.settings?.assistant || {
        niche: '',
        targetAudience: '',
        contentPurpose: '',
        contentTone: ''
    });

    // Persona Summary state
    const [summary, setSummary] = useState<string | null>(p.settings?.personaSummary || null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    // Sync state if profile prop changes (e.g. after refresh)
    useEffect(() => {
        setName(p.name);
        setEmoji(p.avatarEmoji);
        setHandle(p.handle || '');
        setAssistant(p.settings?.assistant || {
            niche: '',
            targetAudience: '',
            contentPurpose: '',
            contentTone: ''
        });
        setSummary(p.settings?.personaSummary || null);
    }, [p]);

    // Fetch summary if missing but onboarding completed
    useEffect(() => {
        let active = true;
        const fetchSummary = async () => {
            if (summary || !p.settings?.assistant?.completed) return;
            setLoadingSummary(true);
            try {
                const response = await apiClient.get('/api/user/persona-summary', {
                    headers: { 'X-Profile-ID': p.id }
                });
                if (active && response.data.personaSummary) {
                    setSummary(response.data.personaSummary);
                }
            } catch (err) {
                console.error('Failed to fetch summary:', p.id, err);
            } finally {
                if (active) setLoadingSummary(false);
            }
        };
        fetchSummary();
        return () => { active = false; };
    }, [p.id, p.settings?.assistant?.completed, summary]);

    const handleAssistantChange = (field: string, value: string) => {
        setAssistant((prev: any) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const updatedSettings = {
                ...p.settings,
                assistant: {
                    ...p.settings?.assistant,
                    ...assistant,
                    completed: p.settings?.assistant?.completed || false
                }
            };
            await updateProfile(p.id, {
                name,
                avatarEmoji: emoji,
                handle: handle.trim(),
                settings: updatedSettings
            });
            toast.success('Persona updated');
            setIsEditing(false);
        } catch (err) {
            toast.error('Failed to save persona settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleResetAI = async () => {
        try {
            await apiClient.delete('/api/user/profile/reset', {
                headers: { 'X-Profile-ID': p.id }
            });
            toast.success('Persona core reset');
            await refreshProfiles();
            setIsResetConfirmOpen(false);
            triggerAssistant('ONBOARDING');
        } catch (err) {
            toast.error('Failed to reset persona');
        }
    };

    const handleDelete = async () => {
        try {
            await deleteProfile(p.id);
            toast.success('Profile deleted');
            onDeleted();
        } catch (err: any) {
            const msg = err.response?.data?.error || 'Failed to delete profile';
            toast.error(msg);
        } finally {
            setIsDeleteConfirmOpen(false);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Identity & AI DNA Section */}
            <ProfileSection
                title="Persona Configuration"
                description="Configure the unique character and AI DNA for this writing persona."
                footer={
                    !p.isDefault && (
                        <div className="flex justify-start pt-4 border-t border-gray-100 mt-8">
                            <button
                                onClick={() => setIsDeleteConfirmOpen(true)}
                                className="text-xs font-medium text-red-500 hover:text-red-700 underline"
                            >
                                Delete this persona
                            </button>
                        </div>
                    )
                }
            >
                <div className="space-y-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Targeting & Character</h3>
                        {!isEditing && (
                            <button onClick={() => setIsEditing(true)} className="text-sm text-blue-600 hover:text-blue-800">Edit Details</button>
                        )}
                    </div>

                    {isEditing ? (
                        <div className="space-y-6 max-w-2xl bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="md:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Persona Name</label>
                                    <FormInput
                                        type="text"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        placeholder="e.g. Content Growth"
                                        disabled={isSaving}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Emoji</label>
                                    <Select
                                        value={emoji}
                                        onChange={setEmoji}
                                        options={EMOJI_OPTIONS}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Profile Handle (for Widget targeting)
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-400 sm:text-sm">@</span>
                                    </div>
                                    <FormInput
                                        type="text"
                                        value={handle}
                                        onChange={e => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-z0-9_-]/g, ''))}
                                        placeholder="marketing, blog, tech"
                                        className="pl-7"
                                        disabled={isSaving}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500 font-medium">Unique readable ID used to filter articles in the external widget.</p>
                            </div>

                            <div className="border-t border-gray-100 pt-6">
                                <AIAssistantSection
                                    isEditing={true}
                                    isSaving={isSaving}
                                    assistant={assistant}
                                    onChange={handleAssistantChange}
                                />
                            </div>

                            <div className="mt-4 flex justify-end gap-3">
                                <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                                <Button onClick={handleSave} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Settings'}</Button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Character</label>
                                    <div className="flex items-center gap-4">
                                        <span className="text-4xl bg-white p-2 rounded-2xl shadow-sm border border-gray-100">{p.avatarEmoji}</span>
                                        <div>
                                            <p className="text-xl font-bold text-gray-900">{p.name}</p>
                                            {p.handle && (
                                                <p className="text-xs font-semibold text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full inline-block mt-1">@{p.handle}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <AIAssistantSection
                                    isEditing={false}
                                    isSaving={false}
                                    assistant={assistant}
                                    onChange={() => { }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </ProfileSection>

            {/* Writing Persona Summary */}
            <ProfileSection
                title="AI Writing Persona Summary"
                description="A summary of how the AI understands this persona's voice and style."
                footer={
                    <div className="flex justify-end pt-4">
                        <button
                            onClick={() => setIsResetConfirmOpen(true)}
                            className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded-md border border-red-100 hover:bg-red-100 transition-colors"
                        >
                            Reset AI Character
                        </button>
                    </div>
                }
            >
                {loadingSummary ? (
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-gray-600">Analyzing writing style...</span>
                    </div>
                ) : summary ? (
                    <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-6">
                        <h4 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                            Character Summary
                        </h4>
                        <p className="text-sm text-blue-800 leading-relaxed italic">
                            "{summary}"
                        </p>
                    </div>
                ) : (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <p className="text-sm text-gray-500">No identity summary generated yet. Complete the onboarding for this persona to see your AI character analysis.</p>
                        <button onClick={() => triggerAssistant('ONBOARDING')} className="mt-2 text-sm text-blue-600 font-medium hover:underline">Start Onboarding →</button>
                    </div>
                )}
            </ProfileSection>

            {/* Modals */}
            {isResetConfirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto border border-gray-100 animate-in fade-in zoom-in duration-200">
                        <h3 className="text-lg font-bold text-gray-900">Reset AI Character?</h3>
                        <p className="mt-3 text-sm text-gray-600">This will clear the AI's understanding of this persona's style. You'll need to redo the onboarding chat. Are you sure?</p>
                        <div className="mt-6 flex justify-end gap-3">
                            <Button variant="outline" onClick={() => setIsResetConfirmOpen(false)}>Cancel</Button>
                            <Button onClick={handleResetAI} variant="destructive">Yes, Reset Character</Button>
                        </div>
                    </div>
                </div>
            )}

            {isDeleteConfirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto border border-gray-100 animate-in fade-in zoom-in duration-200">
                        <h3 className="text-lg font-bold text-gray-900 text-red-600">Delete this Persona?</h3>
                        <p className="mt-3 text-sm text-gray-600">You are about to delete <strong>{p.name}</strong>. This cannot be undone if there are no articles linked to it. Articles linked to this persona will prevent deletion.</p>
                        <div className="mt-6 flex justify-end gap-3">
                            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</Button>
                            <Button onClick={handleDelete} variant="destructive">Confirm Delete</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
